import { useState, useEffect, useRef, useCallback } from 'react';

const MAX_HISTORY = 50;
const DEBOUNCE_MS = 800;

/**
 * Hook historii Undo/Redo. Generyczny — śledzi wszystkie klucze przekazane w `watched`
 * i przywraca przez odpowiadające settery z `setters` (te same nazwy kluczy).
 *
 * Snapshot-based, debouncowany — kolejne zmiany w ciągu DEBOUNCE_MS łączą się w jeden krok.
 * Undo / Redo flushuje pending debounce zanim cofnie, więc nawet bardzo szybkie cofnięcie
 * po zmianie zadziała poprawnie (rejestruje pojedynczą operację jako zakończoną).
 *
 * UWAGA: klucze w `setters` muszą być takie same jak w `watched` (np. { boreholes: setBoreholes }),
 * nie nazwy setterów (`setBoreholes`) — inaczej restore będzie no-op.
 */
export function useUndoRedo(watched, setters) {
  const [undoStack, setUndoStack] = useState([]);
  const [redoStack, setRedoStack] = useState([]);

  const watchedRef = useRef(watched);
  const settersRef = useRef(setters);
  const keysRef = useRef(Object.keys(watched));

  const isRestoringRef = useRef(false);
  const lastStableRef = useRef(null);
  const debounceRef = useRef(null);
  const initRef = useRef(false);

  watchedRef.current = watched;
  settersRef.current = setters;
  keysRef.current = Object.keys(watched);

  const buildSnapshot = () => {
    const snap = {};
    const w = watchedRef.current;
    for (const k of keysRef.current) snap[k] = w[k];
    return snap;
  };

  // Ref żeby Ctrl+Z zawsze trafiał w aktualny restore (omija stale closure w useCallback)
  const doRestoreRef = useRef(null);
  doRestoreRef.current = (snap) => {
    isRestoringRef.current = true;
    const s = settersRef.current;
    for (const k of keysRef.current) {
      if (s[k] && snap[k] !== undefined) {
        s[k](snap[k]);
      }
    }
  };

  if (!initRef.current) {
    lastStableRef.current = buildSnapshot();
    initRef.current = true;
  }

  const flushPending = useCallback(() => {
    if (!debounceRef.current) return;
    clearTimeout(debounceRef.current);
    debounceRef.current = null;
    const oldStable = lastStableRef.current;
    const currentSnap = buildSnapshot();
    let changed = false;
    for (const k of keysRef.current) {
      if (oldStable[k] !== currentSnap[k]) { changed = true; break; }
    }
    if (changed) {
      setUndoStack(prev => {
        const next = [...prev, oldStable];
        if (next.length > MAX_HISTORY) next.shift();
        return next;
      });
      setRedoStack([]);
      lastStableRef.current = currentSnap;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const watchedValues = keysRef.current.map(k => watched[k]);

  useEffect(() => {
    if (isRestoringRef.current) {
      lastStableRef.current = buildSnapshot();
      isRestoringRef.current = false;
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const oldStable = lastStableRef.current;
      const currentSnap = buildSnapshot();
      let changed = false;
      for (const k of keysRef.current) {
        if (oldStable[k] !== currentSnap[k]) { changed = true; break; }
      }
      if (changed) {
        setUndoStack(prev => {
          const next = [...prev, oldStable];
          if (next.length > MAX_HISTORY) next.shift();
          return next;
        });
        setRedoStack([]);
        lastStableRef.current = currentSnap;
      }
      debounceRef.current = null;
    }, DEBOUNCE_MS);

    return () => {};
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, watchedValues);

  const undo = useCallback(() => {
    flushPending();
    setUndoStack(prevUndo => {
      if (prevUndo.length === 0) return prevUndo;
      const restored = prevUndo[prevUndo.length - 1];
      const currentSnapshot = lastStableRef.current;
      doRestoreRef.current(restored);
      setRedoStack(prevRedo => [...prevRedo, currentSnapshot]);
      return prevUndo.slice(0, -1);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flushPending]);

  const redo = useCallback(() => {
    flushPending();
    setRedoStack(prevRedo => {
      if (prevRedo.length === 0) return prevRedo;
      const restored = prevRedo[prevRedo.length - 1];
      const currentSnapshot = lastStableRef.current;
      doRestoreRef.current(restored);
      setUndoStack(prevUndo => [...prevUndo, currentSnapshot]);
      return prevRedo.slice(0, -1);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flushPending]);

  return {
    undo,
    redo,
    canUndo: undoStack.length > 0 || debounceRef.current != null,
    canRedo: redoStack.length > 0,
    undoCount: undoStack.length,
    redoCount: redoStack.length,
  };
}
